import React, { useContext, useMemo, useId } from 'react';
import cn from 'classnames';
import { toSvgColor } from 'apps/sigma2-ui/src/components/svg/types';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { LogoSvg } from 'apps/sigma2-ui/src/components/svg/Logo';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { RoutableLink } from 'apps/sigma2-ui/src/components/ui/RoutableLink';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { SvgProps } from 'apps/sigma2-ui/src/components/svg/types';
import { useResponsive } from 'apps/sigma2-ui/src/lib/hooks/useResponsive';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { OrgSwitcher } from 'apps/sigma2-ui/src/components/ui/OrgSwitcher';
import { OrgSwitcherContext } from 'apps/sigma2-ui/src/components/OrgSwitcherContext';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { useAPIMetaInformation } from 'apps/sigma2-ui/src/state/meta-information';
import { BookmarkOutlineIconSvg } from 'apps/sigma2-ui/src/components/svg/BookmarkOutlineIcon';
import { IconDashboardItemSvg } from 'apps/sigma2-ui/src/components/svg/DashboardItemIcon';
import { useSavedAnalysis } from 'apps/sigma2-ui/src/filters-v2/lib/useSavedAnalysis';
import { isEntitledToDashboards } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToRubricsRead } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToGeolocation } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToRichText } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToSummaryStats } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToTableView } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToProgress } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToSavedAnalysis } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToSolutions } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToSuccessFactors } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToCopilots } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToHeatmap } from 'libs/end-to-end/src/group-entitlements';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material';
import { KeyFactorsIconSvg } from 'apps/sigma2-ui/src/components/svg/KeyFactorsIcon';
import { isCustomChartsEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isRubricsPageEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isGeolocationEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isHeatmapEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isProgressEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isDashboardsEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isKeyFactorsEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { determineEntitledDetailsForFocusAreaPage } from 'libs/end-to-end/src/focus-area-utils';
import { getFocusAreasInSortedOrder } from 'libs/end-to-end/src/focus-area-utils';
import { RubricsIconSvg } from 'apps/sigma2-ui/src/components/svg/RubricsIcon';

export const SIDE_NAVIGATION_TEXT_CLASS_SIZE = 'leading-4 2xl:leading-6';

const SideNavigationTypography = ({
  translatedContent,
  isActive,
}: {
  translatedContent: TranslatedContent;
  isActive?: boolean;
}) => (
  <span className={'whitespace'}>
    <S2Typography
      as={'span'}
      translatedContent={translatedContent}
      variant={['nav-body-7', { '2xl': 'nav-body-7' }]}
      textColorVariant={isActive ? 'royal-700' : undefined}
    />
  </span>
);

// TODO: Soon I'll make a primitive that will give all our SVGs some basic props like "size" and "color".
export const SideNavigationIcon = ({ icon }: { icon: React.FC<SvgProps> }) => {
  const sizeClasses = useResponsive(['w-4 h-4', { '2xl': 'w-4 h-4' }]);
  return icon({
    svg: {
      className: sizeClasses,
    },
  });
};

const ToggleExpansionButton: React.FC<{
  isCollapsed: boolean;
  toggleIsCollapsed: () => void;
}> = ({ isCollapsed, toggleIsCollapsed }) => {
  const language = useLanguage();

  return (
    <button
      onClick={toggleIsCollapsed}
      className={cn(
        'text-gray-600 bg-transparent mx-1 my-1 rounded-lg mt-3',
        isCollapsed ? 'py-2 px-3' : 'py-1 px-3',
      )}
    >
      <div
        className={cn(
          `flex space-x-3 font-sans items-center`,
          isCollapsed && 'justify-center',
        )}
      >
        {!isCollapsed ? (
          <div className="flex space-x-4 items-center">
            <SideNavigationIcon icon={IconCollapseSideNavigationSvg} />
            <SideNavigationTypography
              translatedContent={language.collapse()}
              isActive={false}
            />
          </div>
        ) : (
          <SideNavigationIcon icon={IconExpandSideNavigationSvg} />
        )}
      </div>
    </button>
  );
};

const SideNavigationLink = ({
  icon,
  translatedContent,
  to,
  isCollapsed,
  isExternal,
}: {
  icon: React.ReactNode;
  translatedContent: TranslatedContent;
  to: string;
  isCollapsed: boolean;
  isExternal?: boolean;
}) => {
  return (
    <RoutableLink
      to={to}
      // Acts like a regular <a>: https://reactrouter.com/en/main/components/link#reloaddocument
      reloadDocument={isExternal}
      className={({ isActive }) =>
        cn(
          isActive
            ? 'text-royal-700 bg-royal-100'
            : 'text-gray-600 bg-transparent',
          isCollapsed
            ? 'py-2 px-3 mx-1 my-1  rounded'
            : 'py-1 px-3 mx-1 my-1  rounded',
        )
      }
    >
      {({ isActive }) => (
        <div
          className={cn(
            `flex space-x-3  font-sans items-center`,
            isCollapsed && 'justify-center',
          )}
        >
          <span className={isActive ? 'text-royal-700' : ''}>{icon}</span>
          {!isCollapsed && (
            <SideNavigationTypography
              translatedContent={translatedContent}
              isActive={isActive}
            />
          )}
        </div>
      )}
    </RoutableLink>
  );
};

const SideNavigationButton = ({
  icon,
  translatedContent,
  onClick,
  isCollapsed,
}: {
  icon: React.ReactNode;
  translatedContent: TranslatedContent;
  onClick: () => void;
  isCollapsed: boolean;
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        'text-gray-600 bg-transparent mx-1 my-1 rounded-lg',
        isCollapsed ? 'py-2 px-3' : 'py-1 px-3',
      )}
      type="button"
    >
      <div
        className={cn(
          `flex space-x-3 font-sans items-center`,
          isCollapsed && 'justify-center',
        )}
      >
        <span>{icon}</span>

        {!isCollapsed && (
          <SideNavigationTypography translatedContent={translatedContent} />
        )}
      </div>
    </button>
  );
};

const SideNavigationDivider = () => (
  <hr className="border-0 border-t border-[#E7E8E8] my-2 mx-2" />
);

export const IconForFocusArea = ({
  focusAreaDefinition,
}: {
  focusAreaDefinition: MyFocusAreaDefinition;
}) => {
  switch (focusAreaDefinition.iconConfiguration) {
    case 'hiring':
      return <SideNavigationIcon icon={IconHiring24pxSvg} />;
    case 'promotion':
      return <SideNavigationIcon icon={IconPromotion24pxSvg} />;
    case 'attrition':
      return <SideNavigationIcon icon={IconAttrition24pxSvg} />;
    case 'performance':
      return <SideNavigationIcon icon={IconPerformance24pxSvg} />;
    case 'engagement':
      return <SideNavigationIcon icon={IconEngagement24pxSvg} />;
    case 'compensation':
      return <SideNavigationIcon icon={IconCompensation24pxSvg} />;
    case 'leadership':
      return <SideNavigationIcon icon={IconLeadership24pxSvg} />;
    case 'search':
      return <SideNavigationIcon icon={IconFocusAreaSearch24pxSvg} />;
    case 'target':
      return <SideNavigationIcon icon={IconFocusAreaTarget24pxSvg} />;
    case 'arrests':
      return <SideNavigationIcon icon={IconFocusAreaArrests24pxSvg} />;
    default:
      throw guardExhaustive(focusAreaDefinition.iconConfiguration);
  }
};

export const SideNavigationMenu: React.FC<{
  organizationName: string;
  isCollapsed: boolean;
  toggleIsCollapsed: () => void;
  logoutTapped: () => void;
  toggleIsTreatmentCartActive: () => void;
  nlpGraphAvailable: boolean;
  leadershipFlag: boolean;
  myObservation: MyObservation | null;
  warningIsShowing: boolean;
}> = ({
  isCollapsed,
  toggleIsCollapsed,
  logoutTapped,
  toggleIsTreatmentCartActive,
  nlpGraphAvailable,
  myObservation,
  warningIsShowing,
}) => {
  const language = useLanguage();

  const orgSwitcherProps = useContext(OrgSwitcherContext);

  const { metaInformation } = useAPIMetaInformation();

  const publicDynamicAssetsDomainName = useMemo(
    () => metaInformation?.publicDynamicAssetsDomainName ?? '',
    [metaInformation?.publicDynamicAssetsDomainName],
  );

  const someCopilotsAvailable = useMemo(() => {
    return (
      Boolean(myObservation?.copilots?.length ?? 0) &&
      isEntitledToCopilots({
        features: myObservation?.features ?? [],
      })
    );
  }, [myObservation?.copilots, myObservation?.features]);

  const orderedFocusAreas = getFocusAreasInSortedOrder({
    focusAreas: myObservation?.focusAreas ?? [],
  });

  const visibleFocusAreas = useMemo(
    () =>
      orderedFocusAreas.filter(({ focusArea }) => {
        const entitledDetailsForFocusAreaPage =
          determineEntitledDetailsForFocusAreaPage({
            focusArea,
            observation: myObservation,
          });
        return entitledDetailsForFocusAreaPage.pageEnabled;
      }),
    [orderedFocusAreas, myObservation],
  );

  const isSolutionCenterHidden = useMemo(() => {
    return (
      (myObservation?.hideFocusAreaSolutionsPage ?? true) ||
      !isEntitledToSolutions({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation]);

  const keyFactorsAvailable = useMemo(() => {
    return (
      isKeyFactorsEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToSuccessFactors({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation]);

  const showToolsHeader = useMemo(() => {
    return (
      someCopilotsAvailable || !isSolutionCenterHidden || keyFactorsAvailable
    );
  }, [isSolutionCenterHidden, someCopilotsAvailable, keyFactorsAvailable]);

  const showProgressPage = useMemo(() => {
    return (
      isProgressEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToProgress({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation]);

  const showSavedAnalyses = useMemo(() => {
    return (
      (myObservation?.showSavedAnalysesPage ?? false) &&
      isEntitledToSavedAnalysis({
        features: myObservation?.features ?? [],
      })
    );
  }, [myObservation]);

  const showDashboardsPage = useMemo(() => {
    return (
      isDashboardsEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToDashboards({ features: myObservation?.features ?? [] })
    );
  }, [myObservation]);

  const showExplorePage = useMemo(() => {
    const geolocationAvailable =
      isGeolocationEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToGeolocation({
        features: myObservation?.features ?? [],
        focusArea: null,
      });

    const summaryStatsAvailable =
      isCustomChartsEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToSummaryStats({
        features: myObservation?.features ?? [],
        focusArea: null,
      });

    const tableViewAvailable =
      isCustomChartsEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToTableView({
        features: myObservation?.features ?? [],
        focusArea: null,
      });

    const richTextAvailable =
      isCustomChartsEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToRichText({
        features: myObservation?.features ?? [],
      });

    const heatmapAvailable =
      isHeatmapEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToHeatmap({
        features: myObservation?.features ?? [],
        focusArea: null,
      });

    return (
      geolocationAvailable ||
      summaryStatsAvailable ||
      tableViewAvailable ||
      richTextAvailable ||
      heatmapAvailable
    );
  }, [myObservation]);

  const hasRubricsAccess = useMemo(() => {
    if (!myObservation) {
      return false;
    }

    const entitled = isEntitledToRubricsRead({
      features: myObservation.features,
    });
    if (!entitled) {
      return false;
    }

    return isRubricsPageEnabledForRevision({ revisionFlags: myObservation });
  }, [myObservation]);

  const { savedAnalysesList } = useSavedAnalysis();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // These specific pixels sizes are to prevent the side navigation from jerking as the scroll gets to the top or bottom
  return (
    <nav
      className={cn(`flex flex-col bg-white max-w-[250px] justify-between`, {
        'h-[100vh]': !warningIsShowing,
        'h-[calc(100vh-37px)]': warningIsShowing,
      })}
      data-testid="side-nav"
    >
      <div
        style={{
          backgroundImage:
            'linear-gradient(136.29deg, #012B56 50%, #076DD3 161.31%)',
        }}
        className="h-[4.5rem] rounded-tr-xl"
      >
        <div
          className={cn(
            'h-full flex gap-2 py-3 px-5 items-center',
            isCollapsed ? 'justify-center' : 'justify-start',
          )}
        >
          <LogoSvg svg={{ className: 'h-[1.5rem] w-[1.5rem]' }} />
          {!isCollapsed && (
            <div>
              <Paragraph
                textColor="text-white"
                fontFamily="font-sans"
                className="font-bold h-[1rem]"
              >
                {language.sideNavSigma() + ' ' + language.sideNavSquared()}
              </Paragraph>
            </div>
          )}
        </div>
      </div>
      <OrgSwitcher
        {...orgSwitcherProps}
        isCollapsed={isCollapsed}
        publicDynamicAssetsDomainName={publicDynamicAssetsDomainName}
      />
      <div className={`flex flex-col h-[100vh] pt-2 p-2 overflow-y-auto`}>
        <SideNavigationLink
          to={'/overview'}
          translatedContent={language.overview()}
          icon={<SideNavigationIcon icon={IconHome24pxSvg} />}
          isCollapsed={isCollapsed}
        />

        {showSavedAnalyses && (
          <SideNavigationLink
            to={'/saved-analyses'}
            translatedContent={language.savedCount({
              savedList: savedAnalysesList,
            })}
            icon={<SideNavigationIcon icon={BookmarkOutlineIconSvg} />}
            isCollapsed={isCollapsed}
          />
        )}

        {showDashboardsPage && (
          <SideNavigationLink
            to={'/dashboards'}
            translatedContent={language.dashboards()}
            icon={<SideNavigationIcon icon={IconDashboardItemSvg} />}
            isCollapsed={isCollapsed}
          />
        )}

        {hasRubricsAccess && (
          <SideNavigationLink
            to={'/rubrics'}
            translatedContent={language.listRubricsPageTitle()}
            icon={<SideNavigationIcon icon={RubricsIconSvg} />}
            isCollapsed={isCollapsed}
          />
        )}
        {myObservation && visibleFocusAreas.length > 0 && (
          <>
            <SideNavigationDivider />
            {visibleFocusAreas.map((fa) => {
              return (
                <SideNavigationLink
                  key={fa.focusArea}
                  to={`/${fa.focusArea.toLowerCase()}`}
                  translatedContent={language.dangerousUserInput({
                    value: fa.displayName,
                  })}
                  icon={<IconForFocusArea focusAreaDefinition={fa} />}
                  isCollapsed={isCollapsed}
                />
              );
            })}
          </>
        )}
        {myObservation && (
          <>
            {showToolsHeader && <SideNavigationDivider />}
            {showExplorePage && (
              <SideNavigationLink
                to={'/explore'}
                translatedContent={language.sideNavExploreTitle()}
                icon={<SideNavigationIcon icon={IconExploreSvg} />}
                isCollapsed={isCollapsed}
              />
            )}
            {keyFactorsAvailable && (
              <SideNavigationLink
                to={'/success-factors'}
                translatedContent={language.keyFactorsPageTitle()}
                icon={<SideNavigationIcon icon={KeyFactorsIconSvg} />}
                isCollapsed={isCollapsed}
              />
            )}
            {someCopilotsAvailable && (
              <SideNavigationLink
                to={'/copilot'}
                translatedContent={language.copilot()}
                icon={<SideNavigationIcon icon={IconCopilot24pxSvg} />}
                isCollapsed={isCollapsed}
              />
            )}
            {!isSolutionCenterHidden && (
              <SideNavigationButton
                onClick={toggleIsTreatmentCartActive}
                translatedContent={language.treatmentCenter()}
                icon={<SideNavigationIcon icon={SolutionCenterIconSvg} />}
                isCollapsed={isCollapsed}
              />
            )}
            {showProgressPage && (
              <SideNavigationLink
                to={'/progress'}
                translatedContent={language.progress()}
                icon={<SideNavigationIcon icon={ProgressIconSvg} />}
                isCollapsed={isCollapsed}
              />
            )}

            {nlpGraphAvailable && (
              <SideNavigationLink
                to={'/nlp-graph'}
                translatedContent={language.nlpGraph()}
                icon={<SideNavigationIcon icon={IconCopilot24pxSvg} />}
                isCollapsed={isCollapsed}
              />
            )}
          </>
        )}

        <SideNavigationDivider />
        <SideNavigationButton
          translatedContent={language.logOut()}
          onClick={logoutTapped}
          icon={<SideNavigationIcon icon={IconLogOut24pxSvg} />}
          isCollapsed={isCollapsed}
        />

        <div className="flex-grow self-stretch" />

        {!isMobile && (
          <ToggleExpansionButton
            isCollapsed={isCollapsed}
            toggleIsCollapsed={toggleIsCollapsed}
          />
        )}
      </div>
    </nav>
  );
};

export const IconAttrition24pxGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);
  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        fill={fillColor}
        d="M18.649 24a1.243 1.243 0 0 1-1.243-1.243v-1.865c0-1.505-.659-3.108-2.487-3.108H4.973c-1.827 0-2.486 1.603-2.486 3.108v1.865A1.241 1.241 0 0 1 1.243 24 1.24 1.24 0 0 1 0 22.757v-1.865c0-3.294 2.051-5.594 4.973-5.594h9.946c2.922 0 4.973 2.3 4.973 5.594v1.865A1.245 1.245 0 0 1 18.649 24ZM9.946 5.351a2.489 2.489 0 0 1 .484 4.926 2.491 2.491 0 0 1-2.551-1.058 2.48 2.48 0 0 1 .309-3.139 2.488 2.488 0 0 1 1.758-.729Zm0-2.486c-.983 0-1.944.292-2.763.837a4.979 4.979 0 0 0-2.115 5.105 4.972 4.972 0 0 0 3.908 3.908 4.975 4.975 0 0 0 4.486-8.393 4.965 4.965 0 0 0-3.516-1.457ZM22.72 1.667h-5.25a.29.29 0 0 0-.291.291v1.75c0 .161.13.292.291.292h5.25a.292.292 0 0 0 .292-.292v-1.75a.291.291 0 0 0-.292-.291Z"
      />
    </g>
  );
};

export const IconAttrition24pxSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconAttrition24pxGroup {...props} />
    </svg>
  );
};

export const IconCollapseSideNavigationSvg: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);
  const id = useId();

  return (
    <svg
      width="20"
      height="17"
      viewBox="0 0 20 17"
      xmlns="http://www.w3.org/2000/svg"
      {...(props.svg ?? {})}
    >
      <g clipPath={`url(#${id})`}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.62355 7.35689C5.26592 7.71452 5.26592 8.29282 5.62355 8.65045L9.35597 12.3695C9.70884 12.7301 10.2946 12.7263 10.6504 12.3704C11.0081 12.0128 11.0081 11.4345 10.6504 11.0769L8.49055 8.917L18.0003 8.917C18.5051 8.917 18.917 8.50508 18.917 8.00034C18.917 7.4956 18.5051 7.08367 18.0003 7.08367L8.48388 7.08367L10.6438 4.92378C11.0014 4.56615 11.0014 3.98786 10.6438 3.63023C10.2861 3.2726 9.70785 3.2726 9.35022 3.63023L5.62355 7.35689Z"
          fill={fillColor}
        />
      </g>
      <path
        d="M1 16L1 1"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <defs>
        <clipPath id={id}>
          <rect
            width="16"
            height="16"
            fill="white"
            transform="translate(20 16) rotate(-180)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

export const IconEngagement24pxGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);

  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        d="M11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20ZM15.5 11C16.33 11 17 10.33 17 9.5C17 8.67 16.33 8 15.5 8C14.67 8 14 8.67 14 9.5C14 10.33 14.67 11 15.5 11ZM8.5 11C9.33 11 10 10.33 10 9.5C10 8.67 9.33 8 8.5 8C7.67 8 7 8.67 7 9.5C7 10.33 7.67 11 8.5 11ZM12 17.5C14.33 17.5 16.31 16.04 17.11 14H6.89C7.69 16.04 9.67 17.5 12 17.5Z"
        fill={fillColor}
      />
    </g>
  );
};

export const IconEngagement24pxSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconEngagement24pxGroup {...props} />
    </svg>
  );
};

export const IconExpandSideNavigationSvg: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);
  const id = useId();

  return (
    <svg
      width="20"
      height="17"
      viewBox="0 0 20 17"
      xmlns="http://www.w3.org/2000/svg"
      {...(props.svg ?? {})}
    >
      <g clipPath={`url(#${id})`}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M14.3765 7.35689C14.7341 7.71452 14.7341 8.29282 14.3765 8.65045L10.644 12.3695C10.2912 12.7301 9.70545 12.7263 9.34956 12.3704C8.99193 12.0128 8.99193 11.4345 9.34956 11.0769L11.5095 8.917L1.99967 8.917C1.49494 8.917 1.08301 8.50508 1.08301 8.00034C1.08301 7.4956 1.49494 7.08367 1.99967 7.08367L11.5161 7.08367L9.35623 4.92378C8.9986 4.56615 8.9986 3.98786 9.35623 3.63023C9.71386 3.2726 10.2922 3.2726 10.6498 3.63023L14.3765 7.35689Z"
          fill={fillColor}
        />
      </g>
      <path
        d="M19 16L19 1"
        strokeWidth="2"
        strokeLinecap="round"
        stroke={fillColor}
      />
      <defs>
        <clipPath id={id}>
          <rect
            width="16"
            height="16"
            fill="white"
            transform="matrix(1 -8.74228e-08 -8.74228e-08 -1 0 16)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

export const IconHiring24pxGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);

  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        d="M23.689 3.798h-2.487V1.31a.311.311 0 0 0-.31-.31h-1.244a.309.309 0 0 0-.31.31v2.488h-2.487a.314.314 0 0 0-.22.09.313.313 0 0 0-.091.22v1.243c0 .083.033.162.091.221.059.058.138.09.22.09h2.487v2.487a.307.307 0 0 0 .31.31h1.244a.309.309 0 0 0 .31-.31V5.662h2.487a.314.314 0 0 0 .22-.09.315.315 0 0 0 .091-.221V4.108a.313.313 0 0 0-.091-.22.314.314 0 0 0-.22-.09ZM18.649 24a1.243 1.243 0 0 1-1.243-1.243v-1.865c0-1.505-.659-3.108-2.487-3.108H4.973c-1.827 0-2.486 1.603-2.486 3.108v1.865A1.241 1.241 0 0 1 1.243 24 1.24 1.24 0 0 1 0 22.757v-1.865c0-3.294 2.051-5.594 4.973-5.594h9.946c2.922 0 4.973 2.3 4.973 5.594v1.865A1.245 1.245 0 0 1 18.649 24ZM9.946 5.351a2.489 2.489 0 0 1 .484 4.926 2.491 2.491 0 0 1-2.551-1.058 2.48 2.48 0 0 1 .309-3.139 2.488 2.488 0 0 1 1.758-.729Zm0-2.486c-.983 0-1.944.292-2.763.837a4.979 4.979 0 0 0-2.115 5.105 4.972 4.972 0 0 0 3.908 3.908 4.975 4.975 0 0 0 4.486-8.393 4.965 4.965 0 0 0-3.516-1.457Z"
        fill={fillColor}
      />
    </g>
  );
};

export const IconHiring24pxSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconHiring24pxGroup {...props} />
    </svg>
  );
};

export const IconHome24pxGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);

  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        d="m12.029 4.81 7.729 6.138v8.833h-3.313v-3.312a4.417 4.417 0 0 0-8.832 0v3.312H4.3v-8.833l7.729-6.105V4.81Zm0-2.506a1.107 1.107 0 0 0-.53.143l-8.987 7.1a1.084 1.084 0 0 0-.308.383 1.093 1.093 0 0 0-.112.478v11.04a.55.55 0 0 0 .552.552h6.624a.558.558 0 0 0 .392-.16.557.557 0 0 0 .161-.392v-4.979a2.207 2.207 0 1 1 4.416 0v4.969a.553.553 0 0 0 .553.552h6.624a.554.554 0 0 0 .552-.552V10.397a1.108 1.108 0 0 0-.42-.862l-8.987-7.099a1.11 1.11 0 0 0-.53-.143v.011Z"
        fill={fillColor}
      />
    </g>
  );
};

export const IconHome24pxSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconHome24pxGroup {...props} />
    </svg>
  );
};

export const IconPerformance24pxGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);

  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        fill={fillColor}
        d="M12 2.007A10.583 10.583 0 0 0 1.419 12.588c0 4.091 3.527 9.406 7.054 9.406h7.054c3.527 0 7.054-5.315 7.054-9.406A10.58 10.58 0 0 0 12 2.007Zm2.739 17.635H9.262c-2.752 0-5.492-4.056-5.492-7.195A8.227 8.227 0 0 1 12 4.218a8.229 8.229 0 0 1 8.23 8.229c0 3.139-2.74 7.195-5.491 7.195Z"
      />
      <path
        fill={fillColor}
        d="M13.645 14.312A2.332 2.332 0 0 1 12 15a2.33 2.33 0 0 1-2.334-2.333 2.337 2.337 0 0 1 .689-1.645c.42-.432 4.176-1.82 5.401-2.508a.292.292 0 0 1 .397.396c-.653 1.225-2.077 4.982-2.508 5.402Z"
      />
    </g>
  );
};

export const IconPerformance24pxSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconPerformance24pxGroup {...props} />
    </svg>
  );
};

export const IconPromotion24pxGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);

  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        fill={fillColor}
        d="M18.649 24a1.243 1.243 0 0 1-1.243-1.243v-1.865c0-1.505-.659-3.108-2.487-3.108H4.973c-1.827 0-2.486 1.603-2.486 3.108v1.865A1.241 1.241 0 0 1 1.243 24 1.24 1.24 0 0 1 0 22.757v-1.865c0-3.294 2.051-5.594 4.973-5.594h9.946c2.922 0 4.973 2.3 4.973 5.594v1.865A1.245 1.245 0 0 1 18.649 24ZM9.946 5.351a2.489 2.489 0 0 1 .484 4.926 2.491 2.491 0 0 1-2.551-1.058 2.48 2.48 0 0 1 .309-3.139 2.488 2.488 0 0 1 1.758-.729Zm0-2.486c-.983 0-1.944.292-2.763.837a4.979 4.979 0 0 0-2.115 5.105 4.972 4.972 0 0 0 3.908 3.908 4.975 4.975 0 0 0 4.486-8.393 4.965 4.965 0 0 0-3.516-1.457Zm9.415 7.253h1.75a.292.292 0 0 0 .292-.291V5.452h2.333l-3.418-3.419a.118.118 0 0 0-.164 0l-3.418 3.419h2.333v4.375a.292.292 0 0 0 .292.291Z"
      />
    </g>
  );
};

export const IconPromotion24pxSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconPromotion24pxGroup {...props} />
    </svg>
  );
};

export const IconCompensation24pxGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);

  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        d="M13.166 10.915V6.248c2.147.397 2.333 1.902 2.333 2.24h2.334c0-1.47-1.015-4.118-4.667-4.585V1.792a.295.295 0 0 0-.085-.207.295.295 0 0 0-.207-.085h-1.75a.291.291 0 0 0-.291.292v2.111c-3.01.315-4.667 1.914-4.667 4.597 0 1.983 1.762 4.107 4.667 4.562v4.666c-2.147-.396-2.334-1.901-2.334-2.228H6.166c.047.834.317 1.64.782 2.333a5.221 5.221 0 0 0 3.885 2.252v2.123a.291.291 0 0 0 .291.292h1.75a.295.295 0 0 0 .207-.085.295.295 0 0 0 .085-.207v-2.041c2.333-.245 4.667-1.307 4.667-4.667a5.062 5.062 0 0 0-4.667-4.585ZM8.499 8.5c0-.607 0-1.937 2.334-2.263v4.445C9.409 10.343 8.499 9.398 8.499 8.5Zm4.667 9.263v-4.48a2.767 2.767 0 0 1 2.333 2.217c0 .712 0 1.96-2.333 2.263Z"
        fill={fillColor}
      />
    </g>
  );
};

export const IconCompensation24pxSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconCompensation24pxGroup {...props} />
    </svg>
  );
};

export const IconLeadership24pxGroup: React.FC<SvgProps> = (props) => {
  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        d="M5.90625 17.1562L4.03125 18.0938L2.15625 17.1562V14.8125L4.03125 13.875L5.90625 14.8125V17.1562Z"
        stroke="#696D70"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.84375 22.7812V19.9688L4.03125 19.0312L1.21875 19.9688V22.7812H6.84375Z"
        stroke="#696D70"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.5312 12.4688V15.2812"
        stroke="#696D70"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.1875 17.1562L11.5312 15.2812L13.875 17.1562"
        stroke="#696D70"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20.9062 17.1562L19.0312 18.0938L17.1562 17.1562V14.8125L19.0312 13.875L20.9062 14.8125V17.1562Z"
        stroke="#696D70"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21.8438 22.7812V19.9688L19.0312 19.0312L16.2188 19.9688V22.7812H21.8438Z"
        stroke="#696D70"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.4062 4.96875L11.5312 5.90625L9.65625 4.96875V2.625L11.5312 1.6875L13.4062 2.625V4.96875Z"
        stroke="#696D70"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.3438 10.5938V7.78125L11.5312 6.84375L8.71875 7.78125V10.5938H14.3438Z"
        stroke="#696D70"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
};

export const IconLeadership24pxSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconLeadership24pxGroup {...props} />
    </svg>
  );
};

export const SolutionCenterIconGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);

  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        d="M12.713 3.78927L11.773 4.72927C12.733 5.69594 13.333 7.02927 13.333 8.5026C13.333 11.4493 10.9463 13.8359 7.99967 13.8359C5.05301 13.8359 2.66634 11.4493 2.66634 8.5026C2.66634 5.7826 4.69967 3.5426 7.33301 3.21594V4.5626C5.43967 4.8826 3.99967 6.5226 3.99967 8.5026C3.99967 10.7093 5.79301 12.5026 7.99967 12.5026C10.2063 12.5026 11.9997 10.7093 11.9997 8.5026C11.9997 7.39594 11.553 6.39594 10.8263 5.67594L9.88634 6.61594C10.3663 7.1026 10.6663 7.76927 10.6663 8.5026C10.6663 9.97594 9.47301 11.1693 7.99967 11.1693C6.52634 11.1693 5.33301 9.97594 5.33301 8.5026C5.33301 7.2626 6.18634 6.22927 7.33301 5.92927V7.35594C6.93301 7.58927 6.66634 8.00927 6.66634 8.5026C6.66634 9.23594 7.26634 9.83594 7.99967 9.83594C8.73301 9.83594 9.33301 9.23594 9.33301 8.5026C9.33301 8.00927 9.06634 7.5826 8.66634 7.35594V1.83594H7.99967C4.31967 1.83594 1.33301 4.8226 1.33301 8.5026C1.33301 12.1826 4.31967 15.1693 7.99967 15.1693C11.6797 15.1693 14.6663 12.1826 14.6663 8.5026C14.6663 6.6626 13.9197 4.99594 12.713 3.78927Z"
        fill={fillColor}
      />
    </g>
  );
};

export const SolutionCenterIconSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      xmlns="http://www.w3.org/2000/svg"
      {...(props.svg ?? {})}
    >
      <SolutionCenterIconGroup {...props} />
    </svg>
  );
};

export const IconGlossary24pxGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);

  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        d="M12.051 9.008a4.423 4.423 0 0 0 4.423-4.424 4.425 4.425 0 1 0-4.423 4.424Zm0-6.637a2.22 2.22 0 0 1 2.21 2.213 2.218 2.218 0 0 1-2.21 2.212 2.22 2.22 0 0 1-2.215-2.212c0-1.216.997-2.213 2.215-2.213Zm0 9.458a14.564 14.564 0 0 0-9.959-3.926v12.169c3.851 0 7.347 1.493 9.959 3.928 2.608-2.423 6.103-3.928 9.954-3.928V7.903a14.545 14.545 0 0 0-9.954 3.926Zm7.743 6.173a16.86 16.86 0 0 0-7.743 3.119 16.835 16.835 0 0 0-7.747-3.128v-7.69a12.353 12.353 0 0 1 6.24 3.133l1.507 1.414 1.502-1.404a12.37 12.37 0 0 1 6.241-3.132v7.688Z"
        fill={fillColor}
      />
    </g>
  );
};

export const IconGlossary24pxSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconGlossary24pxGroup {...props} />
    </svg>
  );
};

export const IconLogOut24pxGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);

  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        d="m17.521 6.534-1.549 1.549 2.833 2.845H7.636v2.196h11.169l-2.833 2.834 1.549 1.559 5.491-5.492-5.491-5.491ZM3.181 4.197h8.325V2H3.181A2.204 2.204 0 0 0 .984 4.197v15.607c0 1.208.989 2.196 2.197 2.196h8.325v-2.196H3.181V4.197Z"
        fill={fillColor}
      />
    </g>
  );
};

export const IconLogOut24pxSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconLogOut24pxGroup {...props} />
    </svg>
  );
};

export const IconCopilot24pxGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);
  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        d="M12.2812 1.41504L22.9322 1.41504"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1.06738 1.41504H6.39284"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.49902 8.51562H3.83629"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="12.2675"
        cy="5.40913"
        r="3.99409"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.0371 8.51562L20.6998 8.51563"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
};

export const IconCopilot24pxSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      width="24"
      height="11"
      viewBox="0 0 24 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...(props.svg ?? {})}
    >
      <IconCopilot24pxGroup {...props} />
    </svg>
  );
};

export const IconFocusAreaSearch24pxGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);

  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15 18V16.6667C15 15.9594 14.7893 15.2811 14.4142 14.781C14.0391 14.281 13.5304 14 13 14H9C8.46957 14 7.96086 14.281 7.58579 14.781C7.21071 15.2811 7 15.9594 7 16.6667V18"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11 11C12.1046 11 13 10.1046 13 9C13 7.89543 12.1046 7 11 7C9.89543 7 9 7.89543 9 9C9 10.1046 9.89543 11 11 11Z"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20 20L16.65 16.65"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
};

export const IconFocusAreaSearch24pxSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconFocusAreaSearch24pxGroup {...props} />
    </svg>
  );
};

export const IconFocusAreaTarget24pxGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);

  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
};

export const IconFocusAreaTarget24pxSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconFocusAreaTarget24pxGroup {...props} />
    </svg>
  );
};

export const IconFocusAreaArrests24pxGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);

  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        d="M3.36893 20.4888C1.9582 19.7254 1 18.2325 1 16.5158C1 14.0226 3.02116 12.0014 5.51439 12.0014C8.00762 12.0014 10.0288 14.0226 10.0288 16.5158C10.0288 18.8315 8.28525 20.7399 6.03933 21"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.36893 20.4888C1.9582 19.7254 1 18.2325 1 16.5158C1 14.0226 3.02116 12.0014 5.51439 12.0014C8.00762 12.0014 10.0288 14.0226 10.0288 16.5158C10.0288 18.8315 8.28525 20.7399 6.03933 21"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.876 15.5348C17.3924 16.1446 15.63 15.9425 14.3004 14.8567C12.3692 13.2797 12.0821 10.4358 13.6591 8.50468C15.2361 6.57353 18.0799 6.28642 20.0111 7.86339C21.8047 9.32806 22.1801 11.8856 20.961 13.7897"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.25708 12.0014H8.77152"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.2139 10.088L15.8203 5.48155"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="5.32996"
        y1="8.74109"
        x2="5.32996"
        y2="11.0014"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="5.32996"
        y1="6.37372"
        x2="5.32996"
        y2="6.63764"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="6.453"
        y1="4.21771"
        x2="6.453"
        y2="4.48163"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="8.8302"
        y1="4"
        x2="8.8302"
        y2="4.26392"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="12.6251"
        y1="6.75901"
        x2="10.9105"
        y2="5.2862"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
};

export const IconFocusAreaArrests24pxSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconFocusAreaArrests24pxGroup {...props} />
    </svg>
  );
};

export const ProgressIcon: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);

  return (
    <g {...(props.group ?? {})}>
      <path
        d="M23 6L13.5 15.5L8.5 10.5L1 18"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17 6H23V12"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
};

export const ProgressIconSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <ProgressIcon {...props} />
    </svg>
  );
};

export const IconExploreGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);

  // Calculated a relative path so the svg adapts to the size of the container
  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        d="m4.5 6.75c0-.5967.2371-1.169.659-1.591S6.1533 4.5 6.75 4.5H9c.5967 0 1.169.2371 1.591.659.4219.4219.659.9943.659 1.591V9c0 .5967-.2371 1.169-.659 1.591-.422.4219-.9943.659-1.591.659H6.75c-.5967 0-1.169-.2371-1.591-.659C4.7371 10.169 4.5 9.5967 4.5 9V6.75zm11.25 0c0-.5967.237-1.169.6589-1.591.422-.4219.9944-.659 1.5911-.659h2.25c.5967 0 1.1691.2371 1.5911.659.4219.4219.6589.9943.6589 1.591V9c0 .5967-.237 1.169-.6589 1.591-.422.4219-.9944.659-1.5911.659H18c-.5967 0-1.1691-.2371-1.5911-.659-.4219-.422-.6589-.9943-.6589-1.591V6.75zM4.5 18c0-.5967.2371-1.1691.659-1.5911.4219-.4219.9943-.6589 1.591-.6589H9c.5967 0 1.169.237 1.591.6589.4219.422.659.9944.659 1.5911v2.25c0 .5967-.2371 1.1691-.659 1.5911-.422.4219-.9943.6589-1.591.6589H6.75c-.5967 0-1.169-.237-1.591-.6589-.4219-.422-.659-.9944-.659-1.5911V18zm11.25 0c0-.5967.237-1.1691.6589-1.5911.422-.4219.9944-.6589 1.5911-.6589h2.25c.5967 0 1.1691.237 1.5911.6589.4219.422.6589.9944.6589 1.5911v2.25c0 .5967-.237 1.1691-.6589 1.5911-.422.4219-.9944.6589-1.5911.6589H18c-.5967 0-1.1691-.237-1.5911-.6589-.4219-.422-.6589-.9944-.6589-1.5911V18z"
        stroke={fillColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
};

export const IconExploreSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      {...(props.svg ?? {})}
    >
      <IconExploreGroup {...props} />
    </svg>
  );
};

export const IconExploreTableViewGroup: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);

  // Calculated a relative path so the svg adapts to the size of the container
  return (
    <g fill="none" {...(props.group ?? {})}>
      <path
        d="M10.1765 2.08038H6.35294V5.90391H10.1765V2.08038ZM10.9412 2.08038V5.90391H13.6176C13.8288 5.90391 14 6.07509 14 6.28626C14 6.49743 13.8288 6.66862 13.6176 6.66862H10.9412V10.4921H13.6176C13.8288 10.4921 14 10.6633 14 10.8745C14 11.0857 13.8288 11.2569 13.6176 11.2569H10.9412V13.9333C10.9412 14.1445 10.77 14.3157 10.5588 14.3157C10.3477 14.3157 10.1765 14.1445 10.1765 13.9333V11.2569H6.35294V13.9333C6.35294 14.1445 6.18176 14.3157 5.97059 14.3157C5.75942 14.3157 5.58824 14.1445 5.58824 13.9333V11.2569H1.76471V13.9333C1.76471 14.1445 1.59352 14.3157 1.38235 14.3157C1.17119 14.3157 1 14.1445 1 13.9333V1.69803C1 1.48686 1.17119 1.31567 1.38235 1.31567H13.6176C13.8288 1.31567 14 1.48686 14 1.69803C14 1.90919 13.8288 2.08038 13.6176 2.08038H10.9412ZM10.1765 10.4921V6.66862H6.35294V10.4921H10.1765ZM5.58824 2.08038H1.76471V5.90391H5.58824V2.08038ZM5.58824 6.66862H1.76471V10.4921H5.58824V6.66862Z"
        stroke={fillColor}
        strokeWidth="0.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
};

export const IconExploreTableViewSvg: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="15"
      height="15"
      viewBox="0 0 15 15"
      {...(props.svg ?? {})}
    >
      <IconExploreTableViewGroup {...props} />
    </svg>
  );
};
